import { useState } from "react";
import { Loader2, X } from "lucide-react";
import toast from "react-hot-toast";
import { AdminOrderManagementService } from "../../../services/api/admin-order.service";
import type { PatientFamilyMember } from "../../../services/api/admin-order.types";

interface AddFamilyMemberFormProps {
  patientId: string;
  onMemberAdded: (member: PatientFamilyMember) => void;
  onCancel: () => void;
}

const RELATIONS = ["Father", "Mother", "Brother", "Sister", "Spouse", "Son", "Daughter", "Other"];
const GENDERS = ["Male", "Female", "Other"];

const AddFamilyMemberForm: React.FC<AddFamilyMemberFormProps> = ({ patientId, onMemberAdded, onCancel }) => {
  const [form, setForm] = useState({
    name: "",
    relation: "",
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.relation) {
      toast.error("Please enter Name and select Relation");
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("patient_id", patientId);
      formData.append("name", form.name.trim());
      formData.append("relation", form.relation);
      formData.append("date_of_birth", new Date().toISOString());
      formData.append("age", "0");
      formData.append("gender", "Other");
      formData.append("mobile_number", "");
      formData.append("address", "");
      formData.append("pincode", "");
      formData.append("status", "Active");

      const res = await AdminOrderManagementService.addFamilyMember(formData);
      if (res.success) {
        toast.success("Family member added successfully!");
        onMemberAdded(res.data);
      } else {
        toast.error(res.message || "Failed to add family member");
      }
    } catch {
      toast.error("Failed to add family member");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[50] flex items-center justify-center p-4 animate-in" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white">
          <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Add Family Member</h4>
          <button onClick={onCancel} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 bg-transparent border-none cursor-pointer transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 flex flex-col gap-4">
          {/* Name */}
          <div>
            <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Full Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Enter family member's full name"
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-white"
            />
          </div>

          {/* Relation */}
          <div>
            <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Relation *</label>
            <select
              value={form.relation}
              onChange={(e) => handleChange("relation", e.target.value)}
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-white cursor-pointer"
            >
              <option value="">Select Relation</option>
              {RELATIONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-xs font-semibold border border-slate-200 bg-white text-slate-600 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-xs font-semibold bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 cursor-pointer border-none transition-colors disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
          >
            {saving && <Loader2 size={12} className="animate-spin" />}
            {saving ? "Saving..." : "Add Member"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddFamilyMemberForm;
